<template>
  <div class="project68-processing-page">
    <div class="page-header">
      <h1>🏗️ {{ projectName }} Legacy Processing</h1>
      <p>Process and analyze legacy {{ projectName }} documents with comprehensive AI analysis</p>
    </div>

    <!-- Alert Messages -->
    <div v-if="alertMessage" :class="['alert', alertType]">
      {{ alertMessage }}
      <button @click="clearAlert" class="alert-close">×</button>
    </div>

    <!-- Main Processing Component -->
    <Project68BatchProcessor 
      @success="showSuccess"
      @error="showError"
    />

    <!-- Recent Activity -->
    <div v-if="recentActivity && recentActivity.length > 0" class="recent-activity">
      <h3>📋 Recent Activity</h3>
      <div class="activity-list">
        <div 
          v-for="doc in recentActivity" 
          :key="doc.id"
          class="activity-item"
        >
          <div class="activity-icon">📄</div>
          <div class="activity-details">
            <div class="activity-filename">{{ doc.name }}</div>
            <div class="activity-meta">
              <span class="activity-vendor">{{ doc.vendor_name }}</span>
              <span class="activity-time">{{ formatTime(doc.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Project68BatchProcessor from '@/components/Project68BatchProcessor.vue';
import api from '@/services/api';

export default {
  name: 'Project68Processing',
  components: {
    Project68BatchProcessor
  },
  
  data() {
    return {
      alertMessage: '',
      alertType: 'info',
      recentActivity: [],
      projectName: 'Legacy Project'
    };
  },
  
  async mounted() {
    await this.loadRecentActivity();
  },
  
  methods: {
    async loadRecentActivity() {
      try {
        const response = await api.get('/project68/status/');
        this.recentActivity = response.data.recent_activity || [];
        // Extract project name from response (format: "ProjectName (Legacy X → Production Y)")
        if (response.data.project_name) {
          this.projectName = response.data.project_name.split(' (')[0] || response.data.project_name;
        }
      } catch (error) {
        console.error('Error loading recent activity:', error);
      }
    },
    
    showSuccess(message) {
      this.alertMessage = message;
      this.alertType = 'success';
      setTimeout(() => this.clearAlert(), 5000);
      
      // Refresh recent activity
      this.loadRecentActivity();
    },
    
    showError(message) {
      this.alertMessage = message;
      this.alertType = 'error';
    },
    
    clearAlert() {
      this.alertMessage = '';
    },
    
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleString();
    }
  }
};
</script>

<style scoped>
.project68-processing-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-header h1 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 2.5em;
}

.page-header p {
  color: #7f8c8d;
  font-size: 18px;
}

.alert {
  padding: 15px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.alert.success {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.alert.error {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.alert.info {
  background: #d1ecf1;
  border: 1px solid #bee5eb;
  color: #0c5460;
}

.alert-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
}

.alert-close:hover {
  opacity: 1;
}

.recent-activity {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-top: 30px;
}

.recent-activity h3 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.activity-list {
  max-height: 300px;
  overflow-y: auto;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px 0;
  border-bottom: 1px solid #ecf0f1;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  font-size: 20px;
  width: 30px;
  text-align: center;
}

.activity-details {
  flex: 1;
}

.activity-filename {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
}

.activity-meta {
  display: flex;
  gap: 15px;
  font-size: 14px;
  color: #7f8c8d;
}

.activity-vendor {
  font-weight: 500;
}

.activity-time {
  font-style: italic;
}
</style>
